import { Button, Checkbox, Dialog, DialogActions, DialogContent, FormControl, TextField } from "@material-ui/core";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";

import { AjaxResult } from "../../../../../enums/ajaxResult";
import { SiteCommissioningSummary } from "../../../../../dtos/siteCommissioning";
import { FormField } from "../../../../shared/formField";
import { FormSection } from "../../../../shared/formSection";
import { ValidationErrors } from "../../../../shared/validationErrors";
import { useApi } from "../../../../useApi";

import { isPositiveInteger, isValidHostName } from "./validation";

interface Props {
    open: boolean;
    onClose: () => void;
    siteId: number;
    fields: Record<string, unknown>;
    onSaved: (summary: SiteCommissioningSummary) => void;
}

export const EditSystemConfigurationDialog: React.FC<Props> = ({ open, onClose, siteId, fields, onSaved }) => {
    const { siteApi } = useApi();
    const { enqueueSnackbar } = useSnackbar();
    const [computerName, setComputerName] = useState("");
    const [ntpServerAddress, setNtpServerAddress] = useState("");
    const [resetSensorsOnRestart, setResetSensorsOnRestart] = useState(false);
    const [roomSessionTimeoutMinutes, setRoomSessionTimeoutMinutes] = useState("");
    const [motionInRoomTimeoutMinutes, setMotionInRoomTimeoutMinutes] = useState("");
    const [errors, setErrors] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setComputerName(typeof fields.computerName === "string" ? fields.computerName : "");
            setNtpServerAddress(typeof fields.ntpServerAddress === "string" ? fields.ntpServerAddress : "");
            setResetSensorsOnRestart(Boolean(fields.resetSensorsOnRestart));
            setRoomSessionTimeoutMinutes(
                fields.roomSessionTimeoutMinutes !== undefined && fields.roomSessionTimeoutMinutes !== null
                    ? String(fields.roomSessionTimeoutMinutes)
                    : ""
            );
            setMotionInRoomTimeoutMinutes(
                fields.motionInRoomTimeoutMinutes !== undefined && fields.motionInRoomTimeoutMinutes !== null
                    ? String(fields.motionInRoomTimeoutMinutes)
                    : ""
            );
            setErrors([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const save = () => {
        const clientErrors: string[] = [];
        if (computerName && !isValidHostName(computerName)) {
            clientErrors.push("Host name is not a valid host name.");
        }
        if (!isPositiveInteger(roomSessionTimeoutMinutes)) {
            clientErrors.push("Room session timeout is required and must be a positive number.");
        }
        if (!isPositiveInteger(motionInRoomTimeoutMinutes)) {
            clientErrors.push("Motion in room timeout is required and must be a positive number.");
        }
        if (clientErrors.length > 0) {
            setErrors(clientErrors);
            return;
        }

        setSaving(true);
        siteApi
            .updateCommissioning(siteId, [
                {
                    name: "systemConfiguration",
                    items: {
                        config: {
                            fields: {
                                computerName,
                                ntpServerAddress,
                                resetSensorsOnRestart,
                                roomSessionTimeoutMinutes: Number(roomSessionTimeoutMinutes),
                                motionInRoomTimeoutMinutes: Number(motionInRoomTimeoutMinutes),
                            },
                        },
                    },
                },
            ])
            .then((r) => {
                setSaving(false);
                if (r.result === AjaxResult.Success && r.data) {
                    onSaved(r.data);
                    onClose();
                    enqueueSnackbar("System configuration updated", { variant: "success" });
                } else if (r.messages) {
                    setErrors(r.messages);
                } else {
                    enqueueSnackbar("ERROR: Failed to update system configuration", { variant: "error" });
                }
            });
    };

    return (
        <Dialog open={open} disablePortal>
            <DialogContent style={{ minWidth: "400px" }}>
                <FormSection headerText="Edit System Configuration">
                    <FormField label="Host Name">
                        <FormControl style={{ flexGrow: 1 }}>
                            <TextField
                                value={computerName}
                                onChange={(event) => setComputerName(event.currentTarget.value)}
                                variant="outlined"
                                InputProps={{ inputProps: { style: { padding: "8px" } } }}
                            />
                        </FormControl>
                    </FormField>
                    <FormField label="NTP Server">
                        <FormControl style={{ flexGrow: 1 }}>
                            <TextField
                                value={ntpServerAddress}
                                onChange={(event) => setNtpServerAddress(event.currentTarget.value)}
                                variant="outlined"
                                InputProps={{ inputProps: { style: { padding: "8px" } } }}
                            />
                        </FormControl>
                    </FormField>
                    <FormField label="Room Session Timeout (min)" required>
                        <FormControl style={{ flexGrow: 1 }}>
                            <TextField
                                value={roomSessionTimeoutMinutes}
                                onChange={(event) => setRoomSessionTimeoutMinutes(event.currentTarget.value)}
                                variant="outlined"
                                InputProps={{ inputProps: { style: { padding: "8px" } } }}
                            />
                        </FormControl>
                    </FormField>
                    <FormField label="Motion Timeout (min)" required>
                        <FormControl style={{ flexGrow: 1 }}>
                            <TextField
                                value={motionInRoomTimeoutMinutes}
                                onChange={(event) => setMotionInRoomTimeoutMinutes(event.currentTarget.value)}
                                variant="outlined"
                                InputProps={{ inputProps: { style: { padding: "8px" } } }}
                            />
                        </FormControl>
                    </FormField>
                    <FormField label="Reset Sensors on Restart">
                        <Checkbox
                            checked={resetSensorsOnRestart}
                            color="primary"
                            onChange={(event) => setResetSensorsOnRestart(event.target.checked)}
                        />
                    </FormField>
                    <ValidationErrors errors={errors} />
                </FormSection>
            </DialogContent>
            <DialogActions>
                <Button color="primary" variant="contained" onClick={save} disabled={saving}>
                    {saving ? "Saving..." : "Save"}
                </Button>
                <Button color="secondary" variant="contained" onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};
